import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { map, startWith, switchMap, take, tap } from 'rxjs/operators';

import { DsBannerState } from '@levelaccess/design-system';
import { TranslateService } from '../../../../translate/translate.service';
import { ModalService } from '../../../../services/modal.service';
import { TenantService } from '../../../../services/tenant.service';
import { WorkspaceService } from '../../../../services/workspace.service';
import { ErrorMessageService } from '../../../../services/error-message.service';
import { A11yService } from '../../../../services/a11y.service';
import { AngularUtility } from '../../../../utility/angular.utility';
import { MigrateWorkspaceModalComponent } from '../migrate-workspace-modal/migrate-workspace-modal.component';
import { MigrateWorkspaceConfirmationModalComponent } from '../migrate-workspace-confirmation-modal/migrate-workspace-confirmation-modal.component';
import { MigrationTroubleshootModalComponent } from '../migration-troubleshoot-modal/migration-troubleshoot-modal.component';
import { IWorkspaceWithDpCount } from '../../../../../../shared/interfaces/workspace.interface';
import { ITenant } from '../../../../../../shared/interfaces/tenant.interface';
import { SharedCommonUtility } from '../../../../../../shared/utils/common.utility';
import { errorMessagesNames } from '../../../../../../shared/constants/errors';
import { $workspace } from '../../../../../../shared/constants/workspace';
import { Api } from '../../../../../../shared/constants/api';
import { alertType } from '../../../../constants/alert.constants';
import { $tenant } from '../../../../../../shared/constants/tenant';
import { IMapWorkspaceIdToName } from '../../../../../../shared/interfaces/admin.interface';
import { RequiredSecurities } from '../../../../../../shared/constants/required-securities';
import { IRequiredSecurity } from '../../../../../../shared/interfaces/security.interface';
import { FeatureFlagService } from '../../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../../shared/interfaces/feature-flag.interface';
import { AclSecurityAdapter } from '../../../../services/acl.service';
import { UserAclService } from '../../../../services/user-acl.service';
import { TenantWorkspaceService, TenantWorkspaceStatus } from '../../../../services/tenant-workspace.service';

const DESCRIPTIVE_MIGRATION_ERROR_MESSAGES_MAP: Map<string, string> = new Map([
  [errorMessagesNames.CannotMigrateWorkspaceWithNoDPToTenant, 'cannot_migrate_a_workspace_without_a_dp_to_a_tenant'],
  [errorMessagesNames.TenantMissingAcademyIntegration, 'cannot_migrate_workspace_to_tenant_without_academy_integration_setup'],
  [errorMessagesNames.AcademySeatLimitExceeded, 'academy_seat_limit_exceeded_message'],
]);

@Component({
  selector: 'app-tenant-tab-workspaces',
  templateUrl: './tenant-tab-workspaces.component.html',
  styleUrls: ['./tenant-tab-workspaces.component.scss'],
  standalone: false,
})
export class TenantTabWorkspacesComponent implements OnInit, OnDestroy {
  private subscription: Subscription;

  @Input() public tenantId$: Observable<string>;
  @Input() public tenant: ITenant;

  public $workspace: typeof $workspace;
  public form: UntypedFormGroup;
  public Api: typeof Api;
  public workspaces$: Observable<IWorkspaceWithDpCount[]>;
  public reloadWorkspaces$: BehaviorSubject<void>;
  public errorResponseMessages: Array<string>;
  public alertType: typeof alertType;
  public isFiltered: boolean = false;

  public displayMigrateWorkspace$: Observable<boolean>;
  public displayMigrationTroubleshooting$: Observable<boolean>;
  public workspaceCreateRequiredSecurity: IRequiredSecurity;

  protected workspacesCreationMessage$: BehaviorSubject<string | null>;
  protected workspacesCreationBannerState$: BehaviorSubject<DsBannerState>;

  public constructor(
    private translateService: TranslateService,
    private modalService: ModalService,
    private tenantService: TenantService,
    private workspaceService: WorkspaceService,
    private errorMessageService: ErrorMessageService,
    private a11yService: A11yService,
    private router: Router,
    private featureFlagService: FeatureFlagService,
    private userAclService: UserAclService,
    private activatedRoute: ActivatedRoute,
    private tenantWorkspaceService: TenantWorkspaceService,
    formBuilder: UntypedFormBuilder,
  ) {
    this.reloadWorkspaces$ = new BehaviorSubject<void>(undefined);
    this.$workspace = $workspace;
    this.form = formBuilder.group({
      [$workspace.name]: formBuilder.control(''),
    });
    this.Api = Api;
    this.subscription = new Subscription();
    this.errorResponseMessages = [];
    this.alertType = alertType;
    this.workspaceCreateRequiredSecurity = RequiredSecurities.Workspaces_Create;
    this.displayMigrateWorkspace$ = this.workspaceMigrationEnabled$();
    this.displayMigrationTroubleshooting$ = this.workspaceMigrationResyncEnabled$();

    this.workspacesCreationMessage$ = new BehaviorSubject<string | null>(null);
    this.workspacesCreationBannerState$ = new BehaviorSubject<DsBannerState>(DsBannerState.success);
  }

  private workspaceMigrationEnabled$(): Observable<boolean> {
    return this.featureFlagService.flagsChange$.pipe(
      map(() => {
        return this.featureFlagService.variation(FeatureFlagCollection.workspaceMigration, true);
      }),
    );
  }

  private workspaceMigrationResyncEnabled$(): Observable<boolean> {
    return combineLatest([this.canResyncWorkspaceMigration$(), this.featureFlagService.flagsChange$]).pipe(
      map(([canResync]: [boolean, void]) => {
        if (canResync === false) {
          return false;
        }

        return this.featureFlagService.variation(FeatureFlagCollection.workspaceMigrationResync, true);
      }),
    );
  }

  private canResyncWorkspaceMigration$(): Observable<boolean> {
    return this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter.useEntityLevel().useFunctionalActions(RequiredSecurities.Tenants_Update.functionalActions).check(),
        ),
      );
  }

  private onMigrateWorkspaceSuccess(): void {
    this.reloadWorkspaces$.next();
  }

  private onMigrateWorkspaceError(response: HttpErrorResponse): void {
    if (DESCRIPTIVE_MIGRATION_ERROR_MESSAGES_MAP.has(response?.error?.app?.name)) {
      this.handleError(DESCRIPTIVE_MIGRATION_ERROR_MESSAGES_MAP.get(response?.error?.app?.name), response.error.app.params);
      return;
    }

    this.handleGenericError('error_migrate_workspace', response);
  }

  private handleError(message: string, words?: string[]): void {
    const errorMessage = this.translateService.instant(message, words);

    this.a11yService.setMessage(errorMessage);

    this.errorResponseMessages.push(errorMessage);
  }

  private handleGenericError(message: string, response: HttpErrorResponse): void {
    const errorMessage = this.errorMessageService.getGlobalErrorResponse(response);
    const appMessage = this.translateService.instant(message);

    this.a11yService.setMessage(appMessage + ': ' + errorMessage);

    if (typeof errorMessage === 'string') {
      this.errorResponseMessages.push(this.translateService.instant(errorMessage));
    }
  }

  public ngOnInit(): void {
    this.subscription.add(
      this.tenantWorkspaceService.workspacesStatusChange$.subscribe((status: TenantWorkspaceStatus) => {
        if (SharedCommonUtility.notNullish(status.error)) {
          this.workspacesCreationMessage$.next(this.translateService.instant(status.error));
          this.workspacesCreationBannerState$.next(DsBannerState.error);
          this.tenantWorkspaceService.resetWorkspacesStatus();
        } else if (status.workspacesLoaded > 0) {
          this.workspacesCreationMessage$.next(
            this.translateService.instant('workspaces_added', [status.workspacesLoaded, status.digitalPropertiesLoaded]),
          );
          this.workspacesCreationBannerState$.next(DsBannerState.success);
          this.tenantWorkspaceService.resetWorkspacesStatus();
        }
      }),
    );

    const searchString$: Observable<string> = this.form.get($tenant.name).valueChanges.pipe(startWith(null));

    this.workspaces$ = combineLatest([this.tenantId$, searchString$, this.reloadWorkspaces$]).pipe(
      tap(([, searchString]: [string, string, void]) => {
        this.isFiltered = !SharedCommonUtility.isNullishOrEmpty(searchString);
      }),
      switchMap(([tenantId, searchString]: [string, string, void]) => this.tenantService.getWorkspaces(tenantId, searchString)),
      AngularUtility.shareRef<IWorkspaceWithDpCount[]>(),
    );
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public async openMigrateWorkspaceModal(): Promise<void> {
    this.errorResponseMessages = [];

    const modal: MigrateWorkspaceModalComponent = this.modalService.open(MigrateWorkspaceModalComponent).instance;
    modal.tenantId$ = this.tenantId$;
    this.subscription.add(modal.onSelectWorkspace.subscribe(this.confirmWorkspaceMigration.bind(this)));
  }

  public async confirmWorkspaceMigration(workspaces: IMapWorkspaceIdToName): Promise<void> {
    const workspacesIds: string[] = Array.from(workspaces.keys());
    const workspacesNames: string[] = Array.from(workspaces.values());
    const modal: MigrateWorkspaceConfirmationModalComponent = this.modalService.open(
      MigrateWorkspaceConfirmationModalComponent,
    ).instance;
    modal.init(workspacesNames, this.tenant[$tenant.name]);

    await modal.container.onClose$.pipe(take(1)).toPromise();

    if (modal.confirmed && !modal.cancelled) {
      this.subscription.add(
        this.workspaceService.migrateWorkspaceToTenant(workspacesIds, this.tenant[$tenant._id]).subscribe({
          next: this.onMigrateWorkspaceSuccess.bind(this),
          error: this.onMigrateWorkspaceError.bind(this),
        }),
      );
    }
  }

  public async onCreateWorkspace(): Promise<void> {
    await this.router.navigate([Api.createWorkspace], { relativeTo: this.activatedRoute });
  }

  public openMigrationTroubleshootModal(): void {
    const modal: MigrationTroubleshootModalComponent = this.modalService.open(MigrationTroubleshootModalComponent).instance;
    modal.tenant = this.tenant;
  }

  public onWorkspacesCreationBannerClosed(): void {
    this.workspacesCreationMessage$.next(null);
  }
}
